import React, { useEffect, useState, useRef } from 'react'
import {
  CButton,
  CForm,
  CFormInput,
  CInputGroup,
  CInputGroupText,
  CSpinner,
} from '@coreui/react'
import CIcon from '@coreui/icons-react'
import { cilEnvelopeClosed } from '@coreui/icons'
import GuestLayout from '@/layout/GuestLayout'
import { useFormik } from 'formik'
import validationHelper from "@/helpers/validationHelper";
import { toast } from 'react-toastify'
import { useSelector } from 'react-redux'
import { loggedUser } from '@/store/features/userSlice'
import { useNavigate } from "react-router-dom";
import { useForgotPassword } from '@/hooks'
import { dashboard, signIn } from '@/libs/routes'
import { useTranslationContext } from '@/context/TranslationContext'
import { forgot_password } from '@/constants/translation/auth.translation'

/**
 * @name ForgotPassword
 *
 * @description
 * The ForgotPassword component allows users to initiate the process of resetting their password. 
 * It takes the user's email and sends it to the backend to trigger the password reset flow. 
 * If the request is successful, a success message is displayed.
 * The component uses Formik for form handling and validation.
 * 
 * API Response Format:
 * {
 *   success: true,
 *   message: "backend_otp_sent",
 *   data: {
 *     message: "OTP has been sent to your email address",
 *     otp: "123456"  // Only in non-production environments
 *   }
 * }
 *
 * @returns {JSX.Element} The JSX representation of the Forgot Password page.
 */
const ForgotPassword = () => {
  // Translation context to retrieve translated strings.
  const t = useTranslationContext();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionBlockRef = useRef(false);

  // Retrieve logged-in user information from the Redux store.
  const checkLoggenInUser = useSelector(loggedUser);

  // Validation schema for the Forgot Password form using Formik.
  const validationSchema = validationHelper.forgotPasswordValidation();

  // Formik hook for handling form state and submission.
  const forgotPasswordForm = useFormik({
    initialValues: {
      email: "",
    },
    validationSchema,
    onSubmit: (values) => {
      // Block any additional submissions immediately
      if (submissionBlockRef.current) {
        return;
      }
      submissionBlockRef.current = true;
      setIsSubmitting(true);
      doForgotPassword(values);
    },
  });

  /**
   * The API call for submitting the forgot password request.
   * Sends the user's email to trigger the password reset flow.
   * If successful, stores email and encodedToken, then redirects to verify-otp page.
   */
  const { mutate: doForgotPassword, isPending: isLoading } = useForgotPassword(
    (response) => {
      // API returns: { message: "OTP has been sent to your email address", data: { encodedToken: "0b89f5f1-2787-4480-8988-4907c51bf168" } }
      toast.success(response.message || response.data?.message);
      
      // Store email for future use
      localStorage.setItem('forgot_password_email', forgotPasswordForm.values.email);
      
      // Store encodedToken for OTP verification - check both possible locations
      const encodedToken = response.data?.encodedToken || response.encodedToken;
      if (encodedToken) {
        localStorage.setItem('forgot_password_encoded_token', encodedToken);
      } else {
        toast.error('Invalid response from server. Please try again.');
        return;
      }
      
      // Store OTP for development purposes (only in non-production)
      if (response.data?.otp) {
        localStorage.setItem('forgot_password_otp', response.data.otp);
      }
      
      // Redirect to verify-otp page after successful request
      navigate('/verify-otp');
    },
    (error) => {
      const message = error?.response?.data?.message || error?.message || "An error occurred. Please try again.";
      
      // If it's a 500 server error, refresh the page so user can try again
      if (error?.response?.status === 500) {
        toast.error(message || 'User not found');
        setTimeout(() => {
          window.location.reload();
        }, 2000);
        return;
      }
      
      toast.error(message);
    }
  );

  // Redirects logged-in users to the dashboard to prevent access to the Forgot Password page.
  useEffect(() => {
    if (checkLoggenInUser && checkLoggenInUser.isLoggedIn) {
      navigate(dashboard);
    }
  }, [checkLoggenInUser, navigate]);

  // Custom form submit handler with aggressive blocking
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Block any additional submissions immediately
    if (submissionBlockRef.current) {
      return;
    }
    
    // Validate and submit
    if (forgotPasswordForm.isValid) {
      submissionBlockRef.current = true;
      setIsSubmitting(true);
      doForgotPassword(forgotPasswordForm.values);
    } else {
      // Trigger form validation
      forgotPasswordForm.handleSubmit(e);
    }
  };
  const redirectToLogin = () => {
    navigate(signIn);
  };

  return (
    <>
      <CForm className={`mt-4 ${isSubmitting || isLoading ? 'pe-none opacity-50' : ''}`} onSubmit={handleSubmit}>
        <h1>{t(forgot_password.main_heading)}</h1>
        <p className="text-body-secondary">{t(forgot_password.sub_heading)}</p>
        <div className="mb-3">
          <CInputGroup>
            <CInputGroupText>
              <CIcon icon={cilEnvelopeClosed} />
            </CInputGroupText>
            <CFormInput
              placeholder={t(forgot_password.email_placeholder)}
              autoComplete="email"
              type="email"
              name="email"
              className="form-control same-input-cls"
              onChange={forgotPasswordForm.handleChange}
              onBlur={forgotPasswordForm.handleBlur}
              value={forgotPasswordForm.values.email}
              feedbackInvalid={forgotPasswordForm.errors.email ? t(forgotPasswordForm.errors.email) : null} />
          </CInputGroup>
          {/* Display error message if email is invalid */}
          {forgotPasswordForm.errors.email ? (
            <div className="error text-danger mt-1">{t(forgotPasswordForm.errors.email)}</div>
          ) : null}
        </div>

        {/* Submit and Cancel Buttons */}
        <div className="d-flex gap-2 mt-4">
          <CButton color="primary" className="px-4 flex-grow-1 d-flex gap-2 align-items-center justify-content-center" type="submit" disabled={isLoading || isSubmitting}>
            {isLoading && <CSpinner size="sm" variant="grow" />} {t(forgot_password.send_btn_text)}
          </CButton>
          <CButton color="secondary" className="px-4 flex-grow-1" type="button" disabled={isLoading || isSubmitting} onClick={redirectToLogin}>
            {t(forgot_password.cancel_btn_text)}
          </CButton>
        </div>
      </CForm>
    </>
  );
};

/**
 * @name getLayout
 *
 * @description
 * This function is used to wrap the ForgotPassword component with a `GuestLayout`. 
 * This layout is designed specifically for unauthenticated users and provides a consistent design for guest pages.
 * 
 * @param {JSX.Element} page - The current page (ForgotPassword).
 * @returns {JSX.Element} The page wrapped in a `GuestLayout`.
 */
ForgotPassword.getLayout = function getLayout(page) {
  return (
    <GuestLayout>{page}</GuestLayout>
  );
};


export default ForgotPassword;
